import { QueryClient } from "@tanstack/react-query";

/**
 * Exponential backoff delay calculator with minimum delay
 * @param attemptIndex - The current retry attempt (0-indexed)
 * @param baseDelay - Base delay in milliseconds (default: 1000ms)
 * @param maxDelay - Maximum delay cap in milliseconds (default: 30000ms)
 * @param minDelay - Minimum delay to prevent too-rapid retries (default: 0ms)
 */
const calculateBackoffDelay = (
  attemptIndex: number,
  baseDelay = 1000,
  maxDelay = 30000,
  minDelay = 0
): number => {
  // Exponential backoff: baseDelay * 2^attemptIndex
  const delay = Math.min(baseDelay * Math.pow(2, attemptIndex), maxDelay);
  // Ensure minimum delay to prevent rapid-fire retries
  const finalDelay = Math.max(delay, minDelay);
  // Add jitter (±25%) to prevent thundering herd
  const jitter = finalDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.round(finalDelay + jitter);
};

/**
 * Check if error is a 500-level server error
 */
const is500Error = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes("500") ||
      message.includes("502") ||
      message.includes("503") ||
      message.includes("504")
    );
  }
  return false;
};

/**
 * Determine if an error is retryable
 * @param error - The error from the failed request
 */
const isRetryableError = (error: unknown): boolean => {
  // Network errors are retryable
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return true;
  }

  // Check for HTTP status codes if it's a Response error
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    // Don't retry client errors (4xx) except for specific cases
    if (message.includes("401") || message.includes("403")) {
      return false; // Auth errors shouldn't be retried
    }
    if (message.includes("404")) {
      return false; // Not found shouldn't be retried
    }
    if (message.includes("400") || message.includes("422")) {
      return false; // Validation errors shouldn't be retried
    }
    // Retry server errors (5xx) and network issues
    if (
      message.includes("500") ||
      message.includes("502") ||
      message.includes("503") ||
      message.includes("504") ||
      message.includes("network") ||
      message.includes("timeout")
    ) {
      return true;
    }
  }

  // Default to retrying unknown errors (conservative approach)
  return true;
};

/**
 * Configured QueryClient with retry logic and exponential backoff
 * Prevents thundering herd by using longer delays for 500 errors
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry configuration with special handling for 500 errors
      retry: (failureCount, error) => {
        // For 500 errors, retry more times but with longer delays
        // This gives the API more time to recover from cold starts
        if (is500Error(error)) {
          return failureCount < 5; // 5 total attempts (1 initial + 4 retries)
        }
        // For other retryable errors, use 3 retries
        if (failureCount >= 3) return false;
        return isRetryableError(error);
      },
      retryDelay: (attemptIndex, error) => {
        // For 500 errors, use MUCH longer delays to prevent rate limiting
        // Minimum 5s delay, then: 5s, 10s, 20s, 30s, 30s
        // This prevents multiple retries from hitting the API simultaneously
        if (is500Error(error)) {
          // Start with 5 second minimum, then exponential backoff
          return calculateBackoffDelay(attemptIndex, 5000, 30000, 5000);
        }
        // For other errors, use standard backoff with 2s minimum
        return calculateBackoffDelay(attemptIndex, 1000, 30000, 2000);
      },

      // Stale time: 5 minutes before data is considered stale
      staleTime: 5 * 60 * 1000,

      // Cache time: 10 minutes before unused data is garbage collected
      gcTime: 10 * 60 * 1000,

      // Don't refetch on window focus (can be distracting for users)
      refetchOnWindowFocus: false,

      // Refetch on reconnect automatically
      refetchOnReconnect: "always",
    },
    mutations: {
      // Retry mutations only once for network errors
      retry: (failureCount, error) => {
        if (failureCount >= 1) return false;
        return isRetryableError(error);
      },
      retryDelay: (attemptIndex) => calculateBackoffDelay(attemptIndex),
    },
  },
});
